import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import MedDayRow from '@/pages/med/MedDayRow';

/**
 * Pop-up modal that shows a single day for editing.
 * Opens from:
 *   - The Edit button on a muted past-day row (Daily View), and
 *   - A day click on the Calendar tab.
 *
 * Per owner's spec: edits inside this modal do NOT cascade — every
 * time is edited independently, no other rows change unless the user
 * changes them themselves. Save propagates back to the Daily View.
 */
export default function MedDayEditModal({ day, onSave, onClose }) {
  const [draft, setDraft] = useState(day);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSave = async () => {
    await onSave(draft);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      data-testid="med-edit-modal-backdrop"
    >
      <div
        className="w-full max-w-lg bg-white rounded-lg shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid="med-edit-modal"
      >
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200">
          <div className="text-sm font-semibold text-slate-700">Edit day</div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-100 text-slate-500"
            aria-label="Close"
            data-testid="med-edit-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4">
          <MedDayRow day={draft} onChange={setDraft} mode="modal" />
          <p className="mt-3 text-[11px] text-slate-500">
            Each time is edited independently — changes here won't shift the other rows.
          </p>
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-100"
            data-testid="med-edit-modal-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="text-sm px-3 py-1.5 rounded bg-emerald-600 text-white font-medium hover:bg-emerald-700"
            data-testid="med-edit-modal-save"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
